import React from 'react'
import tw from 'tailwind-styled-components'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {cn} from '@containers/html-utils'
import {AccordionItemData} from './types'

const getIconTextColor = (
  opened: boolean,
  disabled: boolean,
  headerStyle: AccordionHeaderStyle
): string => {
  if (disabled) {
    return 'text-disabled'
  } else if (opened || headerStyle === 'filled') {
    return 'text-action-default'
  } else {
    return 'text-secondary'
  }
}

export type AccordionHeaderStyle = 'default' | 'filled'

const TwItem = tw.div`
  min-w-0
  w-full
  mb-8
  last:mb-0
`
TwItem.displayName = 'TwItem'

const filledMixin = cn(
  'cursor-pointer',
  'bg-grey-l80',
  'bg-interactive-l100',
  'py-8',
  'px-12',
  'rounded-4'
)

const defaultMixin = cn(
  'cursor-pointer',
  'bg-grey-l80',
  'hover:bg-interactive-l100',
  'py-8',
  'px-12',
  'rounded-4'
)

const openedMixin = cn(
  'cursor-pointer',
  'bg-transparent',
  'hover:bg-interactive-l100',
  'py-8',
  'px-12',
  'shadow-border-b-1',
  'shadow-grey-l60',
  'rounded-t-4',
  'rounded-b-0',
  'hover:shadow-interactive-l100',
  'hover:rounded-b-none'
)

const disabledMixin = cn(
  'pointer-events-none',
  'bg-transparent',
  'px-12',
  'py-8',
  'shadow-border-1',
  'shadow-grey-l80',
  'rounded-4'
)

const getHeaderStyle = (
  opened: boolean,
  disabled: boolean,
  style: AccordionHeaderStyle
): string => {
  if (style === 'filled') {
    return filledMixin
  }

  if (disabled) {
    return disabledMixin
  } else if (opened) {
    return openedMixin
  } else {
    return defaultMixin
  }
}

const TwHeader = tw.div<{
  $opened: boolean
  $disabled: boolean
  $style: AccordionHeaderStyle
}>`
  min-w-0
  flex
  flex-row
  items-center
  gap-16
  select-none
  ${p => getHeaderStyle(p.$opened, p.$disabled, p.$style)}
`
TwHeader.displayName = 'TwHeader'

const TwHeaderTitle = tw.div<{$disabled?: boolean}>`
  min-w-0
  flex-1
  flex
  items-center
  text-14
  leading-4
  overflow-hidden
  text-ellipsis
  whitespace-nowrap
  ${p => (p.$disabled ? '!text-disabled' : '!text-primary')}
`
TwHeaderTitle.displayName = 'TwHeaderTitle'

const TwContent = tw.div<{$opened: boolean}>`
  overflow-hidden
  ${p => `${p.$opened ? 'pt-8' : 'max-h-0'}`}
`
TwContent.displayName = 'TwContent'

export interface AccordionItemProps {
  item: AccordionItemData
  opened: boolean
  headerStyle?: AccordionHeaderStyle
  onClick: (id: string) => void
  disabled?: boolean
}

export const AccordionItem = React.memo(
  ({
    item,
    opened,
    headerStyle = 'default',
    onClick,
    disabled
  }: AccordionItemProps) => {
    const itemDisabled = disabled || item.disabled || false
    const itemOpened = opened && !itemDisabled
    const itemVisible = item.visible === undefined || item.visible

    return itemVisible ? (
      <TwItem>
        <TwHeader
          $opened={itemOpened}
          $disabled={itemDisabled}
          $style={headerStyle}
          onClick={itemDisabled ? undefined : () => onClick(item.id)}
        >
          <TwHeaderTitle $disabled={itemDisabled}>{item.title}</TwHeaderTitle>
          <IconTabler
            className={cn(
              'flex-none',
              'ml-auto',
              getIconTextColor(itemOpened, itemDisabled, headerStyle)
            )}
            icon={itemOpened ? 'IconChevronUp' : 'IconChevronRight'}
            customSize={16}
            viewBox="0 0 24 24"
          />
        </TwHeader>
        <TwContent $opened={itemOpened}>{item.content}</TwContent>
      </TwItem>
    ) : null
  }
)
